<template>
  <BContainer
    fluid
    class="security-violation-report-page pb-4"
  >
    <section class="mobile-hero mb-3">
      <div class="mobile-hero__icon">
        <i class="fas fa-file-alt" />
      </div>
      <div class="mobile-hero__text">
        <h4 class="mb-1 text-white">
          Report Violation
        </h4>
        <p class="mb-0 text-white">
          Document and submit a student violation incident report.
        </p>
      </div>
    </section>

    <BCard class="mobile-card">
      <BCardBody class="p-3 p-md-4">
        <h6 class="section-title mb-3">
          Create New Violation Report
        </h6>

        <ValidationObserver ref="formObserver">
          <BRow class="g-2">
            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="ctx"
                name="Student"
                rules="required"
              >
                <BFormGroup
                  label="Student"
                  label-class="form-label-mobile"
                >
                  <VSelect
                    v-model="selected.student"
                    :options="options.students"
                    :reduce="v => v.id"
                    label="label"
                    placeholder="Select student"
                  />
                  <small class="text-danger">{{ ctx.errors[0] }}</small>
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="ctx"
                name="Violation Type"
                rules="required"
              >
                <BFormGroup
                  label="Violation Type"
                  label-class="form-label-mobile"
                >
                  <VSelect
                    v-model="selected.violationType"
                    :options="options.violationTypes"
                    :reduce="v => v.id"
                    label="label"
                    placeholder="Select violation type"
                  />
                  <small class="text-danger">{{ ctx.errors[0] }}</small>
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="ctx"
                name="Occurred At"
                rules="required"
              >
                <BFormGroup
                  label="Occurred At"
                  label-class="form-label-mobile"
                >
                  <flat-pickr
                    v-model="form.occurredAtInput"
                    :config="dateTimeConfig"
                    class="form-control"
                  />
                  <small class="text-danger">{{ ctx.errors[0] }}</small>
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <BFormGroup
                label="Location"
                label-class="form-label-mobile"
              >
                <BFormInput
                  v-model="form.location"
                  placeholder="e.g., Main Gate"
                />
              </BFormGroup>
            </BCol>

            <BCol cols="12">
              <BFormGroup
                label="Description"
                label-class="form-label-mobile"
              >
                <BFormTextarea
                  v-model="form.description"
                  rows="3"
                  max-rows="5"
                  placeholder="Describe what happened"
                />
              </BFormGroup>
            </BCol>

            <BCol cols="12">
              <label class="form-label-mobile d-block mb-2">Evidence Image</label>
              <div class="upload-actions d-flex gap-2 flex-wrap mb-2">
                <label
                  class="image-upload-btn image-upload-btn--camera mb-0"
                  @click.prevent="openCameraModal"
                >
                  <i class="fas fa-camera me-1" /> Take Photo
                </label>

                <label class="image-upload-btn mb-0">
                  <i class="fas fa-upload me-1" /> Image Upload
                  <input
                    id="security-image-upload-input"
                    type="file"
                    accept="image/*"
                    class="image-upload-input"
                    @change="onImagePicked"
                  >
                </label>
              </div>

              <BButton
                v-if="form.evidenceFile"
                size="sm"
                variant="outline-danger"
                class="mt-1"
                @click.prevent="clearImage"
              >
                <i class="fas fa-times me-1" /> Remove
              </BButton>

              <div
                v-if="imagePreview"
                class="preview-wrap mt-2"
              >
                <img
                  :src="imagePreview"
                  alt="Evidence Preview"
                  class="preview-img"
                >
                <small class="d-block mt-1 text-muted">{{ form.evidenceFile ? form.evidenceFile.name : '' }}</small>
                <small class="d-block text-muted">{{ evidenceMetaText }}</small>
              </div>

              <b-modal
                v-model="cameraModalVisible"
                title="Take Photo"
                hide-footer
                size="lg"
                content-class="camera-modal-content"
              >
                <div class="camera-modal-body">
                  <div
                    ref="cameraStage"
                    class="camera-stage"
                  >
                    <video
                      ref="cameraVideo"
                      autoplay
                      playsinline
                      muted
                      class="camera-video"
                    />
                    <div class="camera-guide" />
                  </div>
                  <div class="camera-actions d-flex justify-content-center mt-3">
                    <b-button
                      size="sm"
                      variant="primary"
                      @click="capturePhoto"
                    >
                      Capture
                    </b-button>
                    <b-button
                      size="sm"
                      variant="secondary"
                      class="ms-2"
                      @click="closeCameraModal"
                    >
                      Cancel
                    </b-button>
                  </div>
                </div>
              </b-modal>
            </BCol>
          </BRow>

          <div class="mobile-submit-wrap mt-3 d-flex gap-2">
            <BButton
              variant="success"
              :disabled="state.submitting"
              @click.prevent="submitReport"
            >
              <i class="fas fa-paper-plane me-1" />
              {{ state.submitting ? 'Submitting...' : 'Submit Report' }}
            </BButton>
            <BButton
              variant="outline-secondary"
              :disabled="state.submitting"
              @click="$router.go(-1)"
            >
              Cancel
            </BButton>
          </div>
        </ValidationObserver>
      </BCardBody>
    </BCard>
  </BContainer>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import imageCompression from 'browser-image-compression'
import MxFormatter from '@/mixins/formatter'
import { SSharedViolation, SSharedList } from '@/services/shared'

export default {
  name: 'SecurityViolationReport',

  components: {
    ValidationObserver,
    ValidationProvider
  },

  mixins: [MxFormatter],

  middleware: ['auth', 'security-personel'],

  metaInfo: () => ({ title: 'Report Violation' }),

  data () {
    return {
      state: {
        submitting: false
      },
      selected: {
        student: null,
        violationType: null
      },
      options: {
        students: [],
        violationTypes: []
      },
      form: {
        occurredAtInput: '',
        location: '',
        description: '',
        evidenceFile: null
      },
      imagePreview: '',
      cameraModalVisible: false,
      cameraStream: null,
      evidenceMeta: {
        originalSize: 0,
        compressedSize: 0
      }
    }
  },

  computed: {
    dateTimeConfig () {
      return {
        enableTime: true,
        time_24hr: false,
        dateFormat: 'Y-m-d H:i',
        altInput: true,
        altFormat: 'M j, Y h:i K',
        minuteIncrement: 1,
        disableMobile: true
      }
    },
    evidenceMetaText () {
      if (!this.form.evidenceFile) return ''
      const original = this.formatFileSize(this.evidenceMeta.originalSize)
      const compressed = this.formatFileSize(this.evidenceMeta.compressedSize || this.form.evidenceFile.size)
      return `Original: ${original} • Upload: ${compressed}`
    }
  },

  mounted () {
    this.initializeOptions()
  },

  beforeDestroy () {
    if (this.imagePreview) {
      URL.revokeObjectURL(this.imagePreview)
    }
    this.stopCamera()
  },

  methods: {
    async initializeOptions () {
      const [studentsResponse, typesResponse] = await Promise.all([
        SSharedList.getActiveStudents({ per_page: 500, sort: 'last_name', sort_desc: false }).catch(() => ({ data: { data: [] } })),
        SSharedList.getActiveViolationTypes({ per_page: 500, sort: 'name', sort_desc: false }).catch(() => ({ data: { data: [] } }))
      ])

      const students = studentsResponse?.data?.data || studentsResponse?.data?.items || []
      const violationTypes = typesResponse?.data?.data || typesResponse?.data?.items || []

      this.options.students = students.map(student => ({
        ...student,
        label: `${student.last_name}, ${student.first_name}${student.id_number ? ` (${student.id_number})` : ''}`
      }))

      this.options.violationTypes = violationTypes.map(type => ({
        ...type,
        label: `${type.name}${type.severity_level ? ` (${type.severity_level})` : ''}`
      }))
    },

    async onImagePicked (event) {
      const target = event && event.target ? event.target : null
      const file = target && target.files && target.files[0] ? target.files[0] : null
      if (!file) return

      if (!file.type || !file.type.startsWith('image/')) {
        this.swalError('Please select a valid image file.')
        if (target) target.value = ''
        return
      }

      this.form.evidenceFile = file
      this.evidenceMeta.originalSize = file.size
      this.evidenceMeta.compressedSize = 0

      if (this.imagePreview) {
        URL.revokeObjectURL(this.imagePreview)
      }
      this.imagePreview = URL.createObjectURL(file)

      if (target) {
        target.value = ''
      }
    },

    async ensureCompressedEvidence () {
      if (!this.form.evidenceFile) return

      const original = this.form.evidenceFile
      const compressionOptions = {
        maxSizeMB: 0.7,
        maxWidthOrHeight: 1920,
        initialQuality: 0.82,
        useWebWorker: true,
        fileType: 'image/jpeg'
      }

      try {
        const compressed = await imageCompression(original, compressionOptions)
        const fileName = this.toJpegFileName(original.name)
        const uploadFile = new File([compressed], fileName, {
          type: 'image/jpeg',
          lastModified: Date.now()
        })

        this.form.evidenceFile = uploadFile
        this.evidenceMeta.originalSize = this.evidenceMeta.originalSize || original.size
        this.evidenceMeta.compressedSize = uploadFile.size

        if (this.imagePreview) {
          URL.revokeObjectURL(this.imagePreview)
        }
        this.imagePreview = URL.createObjectURL(uploadFile)
      } catch {
        this.evidenceMeta.compressedSize = original.size
      }
    },

    toJpegFileName (name) {
      if (!name) return `evidence-${Date.now()}.jpg`
      const sanitized = name.replace(/\.[^/.]+$/, '')
      return `${sanitized || `evidence-${Date.now()}`}.jpg`
    },

    clearImage () {
      if (this.imagePreview) {
        URL.revokeObjectURL(this.imagePreview)
      }
      this.form.evidenceFile = null
      this.imagePreview = ''
      this.evidenceMeta.originalSize = 0
      this.evidenceMeta.compressedSize = 0
    },

    async openCameraModal () {
      this.cameraModalVisible = true
      await this.$nextTick()
      this.startCamera()
    },

    async startCamera () {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return
        const constraints = { video: { facingMode: { ideal: 'environment' } }, audio: false }
        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        this.cameraStream = stream
        const video = this.$refs.cameraVideo
        if (video) {
          try { video.srcObject = stream } catch (e) { video.src = window.URL.createObjectURL(stream) }
          await video.play().catch(() => { /* ignore play errors */ })
        }
      } catch (err) {
        this.swalError('Unable to access camera. Please allow camera permission or use Image Upload.')
        this.cameraModalVisible = false
      }
    },

    stopCamera () {
      try {
        if (this.cameraStream && this.cameraStream.getTracks) {
          this.cameraStream.getTracks().forEach(t => t.stop())
        }
      } catch (e) { /* ignore stop errors */ }
      this.cameraStream = null
      const video = this.$refs.cameraVideo
      if (video) {
        try { video.srcObject = null } catch (e) { video.src = '' }
      }
    },

    closeCameraModal () {
      this.cameraModalVisible = false
      this.stopCamera()
    },

    capturePhoto () {
      const video = this.$refs.cameraVideo
      const stage = this.$refs.cameraStage
      if (!video || !stage) return

      const stageW = stage.clientWidth
      const stageH = stage.clientHeight
      const vw = video.videoWidth || 1280
      const vh = video.videoHeight || 720

      const displayScale = Math.max(stageW / vw, stageH / vh)
      const sWidth = Math.round(stageW / displayScale)
      const sHeight = Math.round(stageH / displayScale)
      const sx = Math.max(0, Math.round((vw - sWidth) / 2))
      const sy = Math.max(0, Math.round((vh - sHeight) / 2))

      const dpr = window.devicePixelRatio || 1
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(stageW * dpr)
      canvas.height = Math.round(stageH * dpr)
      const ctx = canvas.getContext('2d')
      ctx.scale(dpr, dpr)
      ctx.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, stageW, stageH)

      canvas.toBlob(blob => {
        if (!blob) {
          this.swalError('Unable to capture photo.')
          return
        }
        const fileName = this.toJpegFileName('capture.jpg')
        const file = new File([blob], fileName, { type: 'image/jpeg', lastModified: Date.now() })
        this.form.evidenceFile = file
        this.evidenceMeta.originalSize = file.size
        this.evidenceMeta.compressedSize = 0
        if (this.imagePreview) URL.revokeObjectURL(this.imagePreview)
        this.imagePreview = URL.createObjectURL(file)
        this.closeCameraModal()
      }, 'image/jpeg', 0.92)
    },

    formatFileSize (bytes) {
      if (!bytes) return '0 B'
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    },

    toApiDateTime (value) {
      if (!value) return ''
      const normalized = String(value).trim()
      if (!normalized) return ''

      if (normalized.includes('T')) {
        return `${normalized.replace('T', ' ')}:00`
      }

      if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}$/.test(normalized)) {
        return `${normalized}:00`
      }

      const parsed = new Date(normalized)
      if (!Number.isNaN(parsed.getTime())) {
        const y = parsed.getFullYear()
        const m = String(parsed.getMonth() + 1).padStart(2, '0')
        const d = String(parsed.getDate()).padStart(2, '0')
        const h = String(parsed.getHours()).padStart(2, '0')
        const i = String(parsed.getMinutes()).padStart(2, '0')
        return `${y}-${m}-${d} ${h}:${i}:00`
      }

      return normalized
    },

    async submitReport () {
      const form = this.$refs.formObserver
      if (!form) return

      const valid = await form.validate()
      if (!valid) return

      if (!this.selected.student || !this.selected.violationType || !this.form.occurredAtInput) {
        return
      }

      this.state.submitting = true

      try {
        await this.ensureCompressedEvidence()

        const payload = new FormData()
        payload.append('student_id', this.selected.student)
        payload.append('violation_type_id', this.selected.violationType)
        payload.append('occurred_at', this.toApiDateTime(this.form.occurredAtInput))

        if (this.form.location) payload.append('location', this.form.location)
        if (this.form.description) payload.append('description', this.form.description)
        if (this.form.evidenceFile) payload.append('evidence_file', this.form.evidenceFile)

        await SSharedViolation.report(payload)

        this.swalSuccess('Violation report submitted successfully.')
        // Navigate back to violations list after short delay
        setTimeout(() => {
          this.$router.push({ name: 'security.violations' })
        }, 1000)
      } catch {
        this.swalError('Unable to submit violation report.')
      } finally {
        this.state.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.security-violation-report-page {
  --osas-green-700: #0b7a3d;
  --osas-green-600: #149647;
  --osas-gold-500: #f2b705;
  --osas-gold-100: #fff4cc;
  --osas-surface: #ffffff;
  --osas-border: #e9ecef;
  --osas-text: #1f2937;
  --osas-muted: #6b7280;
  background: linear-gradient(180deg, #f7fbf5 0%, #fdfdf9 100%);
}

.mobile-hero {
  background: linear-gradient(135deg, var(--osas-green-700), var(--osas-green-600));
  border-radius: 1rem;
  padding: 1rem;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  color: #fff;
  box-shadow: 0 14px 30px rgba(20, 150, 71, 0.25);
}

.mobile-hero__icon {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-hero__text h4 {
  color: #fff;
  font-size: 1.05rem;
  font-weight: 700;
}

.mobile-hero__text p {
  color: #fff;
  font-size: 0.8rem;
  opacity: 0.92;
}

.mobile-card {
  border: 1px solid var(--osas-border);
  border-radius: 1rem;
  box-shadow: 0 8px 16px rgba(19, 23, 32, 0.06);
}

.section-title {
  color: var(--osas-text);
  font-weight: 700;
}

.form-label-mobile {
  margin-bottom: 0.35rem;
  color: var(--osas-muted);
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.preview-wrap {
  border: 1px solid var(--osas-border);
  border-radius: 0.75rem;
  padding: 0.65rem;
  background: #fff;
  max-width: 360px;
  margin-inline: auto;
}

.preview-img {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  object-position: center;
  border-radius: 0.5rem;
}

.camera-modal-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.25rem;
}

.camera-modal-content {
  border-radius: 1rem;
}

.camera-stage {
  position: relative;
  width: min(88vw, 360px);
  padding-top: min(88vw, 360px);
  overflow: hidden;
  border-radius: 0.8rem;
  background: #000;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.35);
}

.camera-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform-origin: center center;
}

.camera-guide {
  position: absolute;
  inset: 12%;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.28);
  pointer-events: none;
}

.camera-actions .btn {
  min-width: 104px;
}

.image-upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.45rem 0.9rem;
  border-radius: 0.65rem;
  background: #ffffff;
  color: var(--osas-green-700);
  border: 1px solid var(--osas-border);
  cursor: pointer;
  font-weight: 600;
  position: relative;
  overflow: visible;
  min-height: 38px;
}

.image-upload-btn--camera {
  border-color: var(--osas-gold-500);
  color: #6b5200;
  background: var(--osas-gold-100);
}

.image-upload-btn:hover {
  background: var(--osas-gold-100);
}

.image-upload-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 1000;
}

.mobile-submit-wrap .btn {
  font-weight: 700;
  border-radius: 0.7rem;
  background: linear-gradient(135deg, var(--osas-green-700), var(--osas-green-600));
  border: none;
}

@media (min-width: 768px) {
  .mobile-hero {
    padding: 1.25rem;
    align-items: center;
  }

  .mobile-hero__icon {
    width: 52px;
    height: 52px;
    min-width: 52px;
    font-size: 1.2rem;
  }

  .mobile-hero__text h4 {
    font-size: 1.25rem;
  }

  .mobile-hero__text p {
    font-size: 0.9rem;
  }
}
</style>
